import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import PhoneInput, {
  ICountry,
  IPhoneInputRef,
} from 'rn-international-phone-number';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import NetInfo from '@react-native-community/netinfo';
import { axiosinstance } from '../../axiosClient';
import { useResetPasscode } from '../../Context/ResetPasscodeContext';
import ErrorContext from '../../Context/ErrorContext';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import Typography from '../../components/Reusables/Typography';
import { PREFERRED_COUNTRIES } from '../../utils/phone';
import { useDetectedCountry } from '../../hooks/useDetectedCountry';
import { shouldUseWebLayout } from '../../utils/layoutUtils';
import { getApiMessage } from '../../utils/apiError';
import { borderWidth, fs, s, v } from '../../utils/libs';
import { GTFonts, theme } from '../../theme';

const ResetPasscodeRequestScreen = ({ navigation }: { navigation: any }) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 768;
  const useWebLikeLayout = shouldUseWebLayout(windowWidth, windowHeight);
  const { handleError } = useContext(ErrorContext);
  const { setSession, clearSession } = useResetPasscode();

  const [phoneInputValue, setPhoneInputValue] = useState('');
  const [phoneSelectedCountry, setPhoneSelectedCountry] = useState<
    ICountry | undefined
  >(undefined);
  const phoneInputRef = React.useRef<IPhoneInputRef | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validPhone, setValidPhone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const { countryCode, iCountry } = useDetectedCountry();

  useEffect(() => {
    if (!phoneSelectedCountry && iCountry) {
      setPhoneSelectedCountry(iCountry);
    }
  }, [phoneSelectedCountry, iCountry]);

  useEffect(() => {
    setValidPhone(!!phoneInputRef.current?.isValidPhoneNumber);
  }, [phoneNumber]);

  const buildE164From = (
    input: string,
    countryOverride?: ICountry | undefined,
  ): string => {
    const digitsOnly = String(input || '').replace(/[^0-9]/g, '');
    const country = countryOverride || phoneInputRef.current?.getCountry?.();
    const root = (country as any)?.idd?.root as string | undefined;
    const suffixes = (((country as any)?.idd?.suffixes as string[]) ||
      []) as string[];
    let code = '';
    if (root) {
      code = `${root}${suffixes?.[0] ?? ''}`.replace(/\s+/g, '');
      if (!code.startsWith('+')) code = `+${code}`;
    }
    if (code && digitsOnly) return `${code}${digitsOnly}`;
    if (digitsOnly) return `+${digitsOnly}`;
    return '';
  };

  const handleSubmit = async () => {
    setErrMsg('');
    const isPhoneValid = !!phoneInputRef.current?.isValidPhoneNumber;
    if (!isPhoneValid || !phoneNumber) {
      setErrMsg('Please enter a valid phone number');
      return;
    }

    const netState = await NetInfo.fetch();
    const isOnline = !!(
      netState.isConnected &&
      (netState.isInternetReachable ?? false)
    );
    if (!isOnline) {
      handleError(
        'No internet connection. Please check your data or wifi and try again.',
        'error',
        true,
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        bp_phone_number: phoneNumber,
      };
      console.log(
        'bp_owner_password_reset_code_information',
        'payload',
        payload,
      );
      const response = await axiosinstance.post(
        'bp_owner_password_reset_code_information',
        JSON.stringify(payload),
      );
      console.log('bp_owner_password_reset_code_information', response);

      if (response?.data?.status === 200 && response?.data?.id) {
        setSession({
          phoneE164: phoneNumber,
          ownerUserId: String(response.data.id),
        });
        navigation.navigate('ResetPasscodeVerifyCode');
        return;
      }

      setErrMsg(
        typeof response?.data?.message === 'string'
          ? response.data.message
          : 'Unable to send reset code',
      );
    } catch (e: any) {
      console.log(
        'bp_owner_password_reset_code_information error',
        e?.response?.status,
        e?.response?.data ?? e,
      );
      const status = e?.response?.status;
      const backendMsg = e?.response?.data?.message;
      if (status === 403 && typeof backendMsg === 'string' && backendMsg) {
        setErrMsg(backendMsg);
        return;
      }
      setErrMsg(getApiMessage(e, 'Unable to send reset code'));
    } finally {
      setLoading(false);
    }
  };

  const goBackToLogin = useCallback(() => {
    clearSession();
    navigation.navigate('Login');
  }, [clearSession, navigation]);

  const webForm = (
    <View style={styles.webFormCard}>
      <Typography style={styles.webTitle}>Reset passcode</Typography>
      <Typography style={styles.webSubtitle}>
        Enter the phone number used for account creation. We will send a
        verification code.
      </Typography>
      {errMsg ? (
        <View style={styles.errorBanner}>
          <Typography style={styles.errorText}>{errMsg}</Typography>
        </View>
      ) : null}
      <Typography style={styles.label}>Phone Number</Typography>
      <PhoneInput
        phoneInputStyles={{
          container: {
            height: 52,
            backgroundColor: theme.colors.surface,
            borderWidth: 1,
            borderColor: theme.colors.borderGray200,
            borderRadius: 12,
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          },
          input: {
            fontFamily: GTFonts.semibold,
            fontSize: fs(16),
            lineHeight: fs(20),
            color: theme.colors.textGray900,
            outlineStyle: 'none',
          } as any,
          callingCode: {
            fontFamily: GTFonts.semibold,
            fontSize: fs(16),
            color: theme.colors.textGray900,
            lineHeight: fs(20),
          },
        }}
        ref={phoneInputRef}
        defaultCountry={countryCode as any}
        value={phoneInputValue}
        modalType="bottomSheet"
        placeholder="720 000 000"
        popularCountries={PREFERRED_COUNTRIES}
        modalPopularCountriesTitle="Popular countries"
        modalAllCountriesTitle="All countries"
        minBottomsheetHeight={'50%'}
        initialBottomsheetHeight={'80%'}
        maxBottomsheetHeight={'92%'}
        onChangePhoneNumber={val => {
          let v = val;
          if (v.startsWith('0')) v = v.slice(1);
          setPhoneInputValue(v);
          setPhoneNumber(buildE164From(v, phoneSelectedCountry));
        }}
        country={phoneSelectedCountry}
        onChangeCountry={c => {
          setPhoneSelectedCountry(c);
          setPhoneNumber(buildE164From(phoneInputValue, c));
        }}
      />
      <TouchableOpacity
        style={[
          styles.primaryButton,
          (!validPhone || loading) && styles.primaryButtonDisabled,
        ]}
        disabled={!validPhone || loading}
        onPress={() => void handleSubmit()}>
        {loading ? (
          <ActivityIndicator color={theme.colors.textInverse} />
        ) : (
          <Typography style={styles.primaryButtonText}>Send code</Typography>
        )}
      </TouchableOpacity>
      <TouchableOpacity onPress={goBackToLogin} style={styles.linkRow}>
        <Typography style={styles.linkText}>Back to login</Typography>
      </TouchableOpacity>
    </View>
  );

  if (useWebLikeLayout && isLargeScreen) {
    return (
      <View style={styles.webOuter}>
        <KeyboardAwareFormScroll
          contentContainerStyle={styles.webScrollContent}
          keyboardShouldPersistTaps="handled">
          {webForm}
        </KeyboardAwareFormScroll>
      </View>
    );
  }

  return (
    <View style={styles.safeArea}>
      <KeyboardAwareFormScroll
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        baseBottomPadding={v(86)}
        bottomOffset={v(20)}>
        <Typography style={styles.mobileTitle}>Reset passcode</Typography>
        <Typography style={styles.mobileSubtitle}>
          Enter the phone number you used on your account. We will send a
          verification code.
        </Typography>
        {errMsg ? (
          <View style={styles.errorBanner}>
            <Typography style={styles.errorText}>{errMsg}</Typography>
          </View>
        ) : null}
        <Typography style={styles.label}>Phone Number</Typography>
        <PhoneInput
          phoneInputStyles={{
            container: {
              height: v(50),
              backgroundColor: theme.colors.surface,
              borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
              borderColor: theme.colors.borderGray,
              width: '100%',
            },
            input: {
              fontFamily: GTFonts.semibold,
              fontSize: fs(16),
              lineHeight: fs(18),
              ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
            } as any,
            callingCode: {
              fontFamily: GTFonts.semibold,
              fontSize: fs(16),
              lineHeight: fs(18),
            },
          }}
          ref={phoneInputRef}
          defaultCountry={countryCode as any}
          value={phoneInputValue}
          modalType="bottomSheet"
          placeholder="720 000 000"
          popularCountries={PREFERRED_COUNTRIES}
          modalPopularCountriesTitle="Popular countries"
          modalAllCountriesTitle="All countries"
          minBottomsheetHeight={'50%'}
          initialBottomsheetHeight={'80%'}
          maxBottomsheetHeight={'92%'}
          onChangePhoneNumber={val => {
            let v = val;
            if (v.startsWith('0')) v = v.slice(1);
            setPhoneInputValue(v);
            setPhoneNumber(buildE164From(v, phoneSelectedCountry));
          }}
          country={phoneSelectedCountry}
          onChangeCountry={c => {
            setPhoneSelectedCountry(c);
            setPhoneNumber(buildE164From(phoneInputValue, c));
          }}
        />
        <TouchableOpacity
          onPress={goBackToLogin}
          style={[styles.linkRow, { marginTop: v(12) }]}>
          <Typography style={styles.linkText}>Back to login</Typography>
        </TouchableOpacity>
      </KeyboardAwareFormScroll>
      <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
        <View style={styles.stickyButtonContainer}>
          <TouchableOpacity
            style={[
              styles.stickyButton,
              (!validPhone || loading) && styles.primaryButtonDisabled,
            ]}
            disabled={!validPhone || loading}
            onPress={() => void handleSubmit()}>
            {loading ? (
              <ActivityIndicator color={theme.colors.textInverse} />
            ) : (
              <Typography style={styles.primaryButtonText}>
                Send code
              </Typography>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardStickyView>
    </View>
  );
};

export default ResetPasscodeRequestScreen;

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: theme.colors.surface,
  },
  scroll: { flex: 1 },
  scrollContent: {
    paddingHorizontal: s(8),
    paddingTop: v(16),
    flexGrow: 1,
  },
  webOuter: {
    flex: 1,
    backgroundColor: theme.colors.backgroundGray50,
    minHeight: '100%',
  },
  webScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: s(24),
    paddingVertical: v(48),
  },
  webFormCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: s(24),
    padding: s(40),
    width: '100%',
    maxWidth: s(480),
    borderWidth: borderWidth(1),
    borderColor: theme.colors.borderGray200,
  },
  webTitle: {
    fontSize: fs(22),
    lineHeight: fs(26),
    fontFamily: GTFonts.semibold,
    color: theme.colors.textGray900,
    marginBottom: v(8),
  },
  webSubtitle: {
    fontSize: fs(14),
    lineHeight: fs(20),
    color: theme.colors.textGray500,
    marginBottom: v(20),
  },
  mobileTitle: {
    fontSize: fs(22),
    lineHeight: fs(26),
    fontFamily: GTFonts.semibold,
    color: theme.colors.text,
    marginBottom: v(8),
  },
  mobileSubtitle: {
    fontSize: fs(14),
    lineHeight: fs(20),
    color: theme.colors.textSecondary,
    marginBottom: v(16),
  },
  label: {
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.semibold,
    color: theme.colors.textGray700,
    marginBottom: v(8),
  },
  errorBanner: {
    backgroundColor: theme.colors.errorLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(16),
  },
  errorText: {
    color: theme.colors.errorDark,
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
  },
  primaryButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: s(12),
    height: v(48),
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: v(20),
  },
  primaryButtonDisabled: {
    backgroundColor: theme.colors.primaryLight,
  },
  primaryButtonText: {
    color: theme.colors.textInverse,
    fontSize: fs(16),
    lineHeight: fs(20),
    fontFamily: GTFonts.semibold,
  },
  linkRow: {
    alignItems: 'center',
    marginTop: v(16),
  },
  linkText: {
    color: theme.colors.primary,
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.semibold,
    textDecorationLine: 'underline',
  },
  stickyButtonContainer: {
    backgroundColor: theme.colors.surface,
    paddingHorizontal: s(8),
    paddingVertical: s(8),
    borderTopWidth: s(Platform.OS === 'web' ? 0.5 : 1),
    borderTopColor: theme.colors.border,
  },
  stickyButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: s(8),
    height: v(46),
    justifyContent: 'center',
    alignItems: 'center',
  },
});
